/* Sample Kit PDP — infinite carousel.

   Replaces Swiper for the scent, press and UGC carousels. Swiper's loop mode
   combined with slidesPerView:'auto' and centeredSlides proved unreliable here:
   it silently disables the loop when there are too few slides, pads the track
   with blank slides, and leaves navigation and pagination out of step.

   This engine is deterministic instead of adaptive:
     * every slide is the same fixed width, so step = width + gap exactly
     * the list is cloned until the track is at least 3 viewports wide, so
       there is always content beyond both edges — no white space, ever
     * position is a virtual index; when it drifts out of the middle cycle it
       is rebased by exactly one cycle with the transition off. The content
       repeats every cycle, so that jump is invisible by construction.
   Nothing measures clones or guesses, so the arrows can never hit an edge. */
(function () {
  'use strict';

  if (window.__skpLoopInit) return;
  window.__skpLoopInit = true;

  var instances = new WeakMap();

  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v;
  }

  /* ---------------------------------------------------------------- */
  /* Videos: one playing at a time, sound on first click               */
  /* ---------------------------------------------------------------- */

  function wireVideos(root) {
    var unmuteOnPlay = root.getAttribute('data-unmute') !== 'false';

    // Delegated, not per-card: the engine clones slides AFTER this runs, so
    // per-element listeners would only ever reach the originals and every
    // cloned card would be dead. Queried live for the same reason.
    function cards() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-skp-video]'));
    }

    function setMuted(video, sound, muted) {
      video.muted = muted;
      if (!sound) return;
      sound.classList.toggle('is-muted', muted);
      sound.setAttribute('aria-pressed', muted ? 'false' : 'true');
    }

    function stopAll(except) {
      cards().forEach(function (card) {
        if (card === except) return;
        var v = card.querySelector('video');
        if (v) {
          v.pause();
          v.currentTime = 0;
          setMuted(v, card.querySelector('[data-skp-video-sound]'), true);
        }
        card.classList.remove('is-playing');
      });
    }

    // A play() rejection is not always a permissions problem: with preload
    // off the element can simply have no data yet. Muting and retrying covers
    // autoplay policy; waiting for the next frame of data covers the rest.
    function attempt(video, card, sound, muteFirst) {
      if (muteFirst) setMuted(video, sound, true);
      var p = video.play();
      if (!p || !p.catch) return;
      p.catch(function () {
        if (!muteFirst) { attempt(video, card, sound, true); return; }
        if (video.readyState >= 2) { card.classList.remove('is-playing'); return; }
        var done = false;
        function go() {
          if (done) return;
          done = true;
          video.removeEventListener('loadeddata', go);
          video.removeEventListener('canplay', go);
          var again = video.play();
          if (again && again.catch) {
            again.catch(function () { card.classList.remove('is-playing'); });
          }
        }
        video.addEventListener('loadeddata', go);
        video.addEventListener('canplay', go);
        try { video.load(); } catch (err) { /* ignore */ }
      });
    }

    var downX = null, downY = null, downCard = null, downTarget = null;
    root.addEventListener('pointerdown', function (e) {
      downTarget = e.target;
      downCard = e.target.closest ? e.target.closest('[data-skp-video]') : null;
      downX = downCard ? e.clientX : null;
      downY = downCard ? e.clientY : null;
    });

    root.addEventListener('click', function (e) {
      // The click's own target is preferred, but a pointer capture taken by the
      // carousel can retarget the click to an ancestor. The element the gesture
      // started on is then the only reliable record of what was tapped.
      var card = e.target.closest('[data-skp-video]') || downCard;
      var startCard = downCard, startTarget = downTarget;
      downCard = null;
      downTarget = null;
      if (!card) return;
      var video = card.querySelector('video');
      if (!video) return;
      var sound = card.querySelector('[data-skp-video-sound]');

      var onSound = e.target.closest('[data-skp-video-sound]') ||
        (startCard === card && startTarget && startTarget.closest &&
          startTarget.closest('[data-skp-video-sound]'));

      if (onSound) {
        e.stopPropagation();
        setMuted(video, sound, !video.muted);
        return;
      }

      if (downX !== null && typeof e.clientX === 'number') {
        var dx = e.clientX - downX, dy = e.clientY - downY;
        downX = null;
        downY = null;
        if (Math.sqrt(dx * dx + dy * dy) > 8) return;   // that was a drag
      }
      downX = null;
      downY = null;

      if (video.paused) {
        stopAll(card);
        card.classList.add('is-playing');
        if (unmuteOnPlay) setMuted(video, sound, false);
        attempt(video, card, sound, !unmuteOnPlay);
      } else {
        video.pause();
        card.classList.remove('is-playing');
      }
    });

    // 'ended' does not bubble, so listen in the capture phase.
    root.addEventListener('ended', function (e) {
      var card = e.target.closest && e.target.closest('[data-skp-video]');
      if (!card) return;
      card.classList.remove('is-playing');
      e.target.currentTime = 0;
    }, true);
  }

  /* ---------------------------------------------------------------- */
  /* Carousel                                                          */
  /* ---------------------------------------------------------------- */

  function Carousel(root) {
    var viewport = root.querySelector('[data-skp-viewport]');
    var track = root.querySelector('[data-skp-track]');
    if (!viewport || !track) return null;

    // Exclude any clone left in the DOM by an earlier build, or a resize
    // cycle would treat clones as originals and multiply them each time.
    var originals = Array.prototype.slice.call(track.children).filter(function (el) {
      return !el.hasAttribute('data-skp-clone');
    });
    var n = originals.length;
    if (!n) return null;

    var gap = num(root, 'data-gap', 20);
    var centered = root.getAttribute('data-centered') === 'true';
    var speed = num(root, 'data-speed', 500);
    var autoplayMs = num(root, 'data-autoplay', 0);
    var fullWidth = root.getAttribute('data-per-view') === '1';
    var dotCount = parseInt(root.getAttribute('data-dot-count'), 10) || n;

    var cycles = 1;          // how many copies of the list are in the track
    var index = 0;           // virtual slide index
    var slideW = 0, step = 0;
    var timer = null;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function addCycle() {
      var frag = document.createDocumentFragment();
      originals.forEach(function (el) {
        var clone = el.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-skp-clone', '');
        clone.removeAttribute('id');
        frag.appendChild(clone);
      });
      track.appendChild(frag);
      cycles++;
    }

    function measure() {
      slideW = fullWidth ? viewport.clientWidth : originals[0].getBoundingClientRect().width;
      // The gap is read back off the track rather than trusted from the data
      // attribute, so a breakpoint that overrides --skp-slide-gap cannot leave
      // the engine's arithmetic disagreeing with what is actually on screen.
      var css = window.getComputedStyle(track).columnGap;
      var measured = parseFloat(css);
      step = slideW + (isNaN(measured) ? gap : measured);
    }

    // Enough copies that the track always covers the viewport on both sides,
    // with room to spare for a long fast drag.
    function ensureCycles() {
      var need = Math.max(5, Math.ceil((viewport.clientWidth * 4) / (n * step)) + 2);
      while (cycles < need) addCycle();
    }

    function cycleWidth() { return n * step; }
    function minOffset() { return offsetFor(n * Math.floor(cycles / 2) - n); }
    function maxOffset() { return offsetFor(n * Math.floor(cycles / 2) + n); }

    function offsetFor(i) {
      var base = i * step;
      return centered ? base - (viewport.clientWidth - slideW) / 2 : base;
    }

    function apply(animate) {
      track.style.transition = animate && !reduced ? 'transform ' + speed + 'ms cubic-bezier(0.4,0,0.2,1)' : 'none';
      track.style.transform = 'translate3d(' + -offsetFor(index) + 'px,0,0)';
      syncDots();
    }

    // Keep the index inside the middle cycle. Because the sequence repeats
    // every n slides, shifting by n is visually identical — so this jump can
    // never be seen, and the arrows never run out of slides.
    function rebase() {
      var middle = n * Math.floor(cycles / 2);
      while (index < middle - n) index += n;
      while (index >= middle + n) index -= n;
      apply(false);
    }

    function goTo(i, animate) {
      index = i;
      var animated = animate !== false && !reduced;
      apply(animate !== false);
      clearTimeout(track._rebase);
      if (!animated) { rebase(); return; }
      // Wait for the real end of the transition; a timer can fire while the
      // track is still moving, which makes the rebase visible as a snap.
      var done = function (e) {
        if (e && e.target !== track) return;
        track.removeEventListener('transitionend', done);
        clearTimeout(track._rebase);
        rebase();
      };
      track.addEventListener('transitionend', done);
      track._rebase = setTimeout(done, speed + 80);
    }

    function next() { goTo(index + 1); }
    function prev() { goTo(index - 1); }

    /* ---- dots ---- */
    var dotsEl = root.querySelector('[data-skp-dots]');
    var dots = [];
    function buildDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      for (var i = 0; i < dotCount; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'skp-dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', (function (target) {
          return function () {
            // Move to the nearest slide whose position in the cycle matches.
            var cur = ((index % n) + n) % n;
            var delta = target - cur;
            if (delta > n / 2) delta -= n;
            if (delta < -n / 2) delta += n;
            goTo(index + delta);
          };
        })(i));
        dotsEl.appendChild(b);
        dots.push(b);
      }
    }
    function syncDots() {
      if (!dots.length) return;
      var active = ((index % n) + n) % n % dotCount;
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === active);
        d.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    /* ---- drag ---- */
    var dragging = false, startX = 0, startOffset = 0, moved = 0;

    // Browsers start a native image/link drag on mousedown, which hijacks the
    // gesture and drags a ghost of the picture around. Cancelling dragstart
    // kills it for images, videos and anchors alike.
    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    var pointerId = null, captured = false;
    viewport.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 || !e.isPrimary) return;
      dragging = true;
      captured = false;
      moved = 0;
      startX = e.clientX;
      startOffset = offsetFor(index);
      track.style.transition = 'none';
      stopAuto();
      pointerId = e.pointerId;
    });
    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      moved = e.clientX - startX;
      // Capture is taken only once the gesture has clearly become a drag, not
      // on every pointerdown. While a capture is still active at pointerup,
      // WebKit dispatches the follow-up click on the capturing element instead
      // of the element under the finger — which silently ate taps on the video
      // cards. A tap never moves far enough to capture, so its click is
      // delivered normally; a real drag still keeps receiving events after the
      // finger leaves the viewport.
      if (!captured && Math.abs(moved) > 3 && viewport.setPointerCapture) {
        try { viewport.setPointerCapture(pointerId); captured = true; } catch (err) { /* ignore */ }
      }
      var live = startOffset - moved;
      var cw = cycleWidth();
      // Shift the origin by whole cycles while dragging. The content repeats
      // every cycle, so this is invisible, and the drag can continue forever.
      while (live < minOffset()) { startOffset += cw; index += n; live += cw; }
      while (live > maxOffset()) { startOffset -= cw; index -= n; live -= cw; }
      track.style.transform = 'translate3d(' + -live + 'px,0,0)';
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (captured && pointerId !== null && viewport.releasePointerCapture) {
        try { viewport.releasePointerCapture(pointerId); } catch (err) { /* ignore */ }
      }
      captured = false;
      pointerId = null;
      var shifted = Math.round(-moved / step);
      goTo(index + shifted);
      startAuto();
    }

    window.addEventListener('pointerup', endDrag);
    // A cancelled pointer (browser took over the gesture, or the touch was
    // interrupted) must settle the track rather than leave it mid-drag.
    window.addEventListener('pointercancel', endDrag);

    /* ---- autoplay ---- */
    function startAuto() {
      if (!autoplayMs || reduced) return;
      stopAuto();
      timer = setInterval(next, autoplayMs);
    }
    function stopAuto() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);

    /* ---- arrows ---- */
    var nextBtn = root.querySelector('[data-skp-next]');
    var prevBtn = root.querySelector('[data-skp-prev]');
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); stopAuto(); next(); startAuto(); });
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); stopAuto(); prev(); startAuto(); });

    function layout() {
      measure();
      ensureCycles();
      apply(false);
    }

    function start() {
      buildDots();
      measure();
      ensureCycles();
      index = n * Math.floor(cycles / 2);   // begin in the middle cycle
      apply(false);
      startAuto();
      root.classList.add('is-ready');
    }

    var ro = window.ResizeObserver ? new ResizeObserver(layout) : null;
    if (ro) ro.observe(viewport);
    else window.addEventListener('resize', layout);

    // Images and webfonts settle after first paint and change slide widths.
    window.addEventListener('load', layout);

    start();

    return {
      destroy: function () {
        stopAuto();
        if (ro) ro.disconnect();
        track.style.transform = '';
        track.style.transition = '';
        // Remove every clone, so switching back to the desktop grid shows the
        // original slides and nothing else.
        Array.prototype.slice.call(track.querySelectorAll('[data-skp-clone]'))
          .forEach(function (el) { el.parentNode.removeChild(el); });
        if (dotsEl) dotsEl.innerHTML = '';
        cycles = 1;
      },
      layout: layout
    };
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    if (root.hasAttribute('data-skp-bound')) return;
    root.setAttribute('data-skp-bound', '');

    wireVideos(root);

    var only = root.getAttribute('data-only-below');
    if (!only) {
      instances.set(root, Carousel(root));
      return;
    }

    // Grid on desktop, carousel below the breakpoint.
    var mq = window.matchMedia('(max-width: ' + parseInt(only, 10) + 'px)');
    var current = null;
    function sync() {
      // offsetParent is null when the element (or an ancestor) is display:none.
      var visible = root.offsetParent !== null;
      if (mq.matches && visible && !current) {
        current = Carousel(root);
        instances.set(root, current);
      } else if (!mq.matches && current) {
        current.destroy();
        current = null;
        instances.set(root, null);
      }
    }
    sync();
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-skp-loop]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
